// FILE: UserAvatar.tsx
// Purpose: The one way a person is drawn in this app — their picture, or coloured initials when there is none.
// Layer: Shared UI component
// Depends on: the avatar read endpoint and the stored avatar file name (directory entry or auth store).
// Exports: UserAvatar

import { memo } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { resolveServerHttpOrigin } from "~/lib/serverOrigin";
import { cn } from "~/lib/utils";
import { userAvatarFallbackStyle, userAvatarSrc, userInitials } from "./UserAvatar.logic";

export type UserAvatarSize = "xs" | "sm" | "md" | "lg";

/**
 * Box and text size per step. Text is set in absolute pixels rather than
 * inherited, because these render inside surfaces with very different base
 * sizes (a sidebar footer, a message row, a settings table) and initials that
 * scale with the surrounding text stop fitting the circle.
 */
const SIZE_CLASSES: Record<UserAvatarSize, string> = {
  xs: "size-5 text-[9px]",
  sm: "size-6 text-[10px]",
  md: "size-8 text-[12px]",
  lg: "size-16 text-[22px]",
};

interface UserAvatarProps {
  readonly userId: string;
  readonly displayName: string;
  /**
   * The name stored on the account, as it arrives from the user directory,
   * `/auth/me`, or the admin user list. Absent or null means "no picture" and
   * renders the initials directly — no request is made.
   */
  readonly avatarFileName?: string | null | undefined;
  readonly size?: UserAvatarSize;
  readonly className?: string;
  /**
   * Set when the avatar sits next to the person's name, which is the usual
   * case: the name is already the label, and a second copy of it in `alt` only
   * makes screen readers say it twice.
   */
  readonly decorative?: boolean;
}

/**
 * A person, drawn.
 *
 * ── Why the fallback is not an `onError` handler ──────────────────────────
 *
 * Base UI's Avatar already tracks the image's loading status and swaps in the
 * fallback when it errors, which covers the case that actually happens: a row
 * still pointing at a file that was deleted, answered with 404. Writing our
 * own `onError` on top would mean two pieces of state describing the same
 * fact.
 *
 * ── Why no `crossOrigin` ──────────────────────────────────────────────────
 *
 * The read endpoint requires the session cookie. A plain `<img>` sends cookies
 * with the request; adding `crossOrigin="anonymous"` would strip them and turn
 * every avatar into a 401 the moment the page is not served by the server
 * itself. `ProjectSidebarIcon` loads the project favicon the same way, for the
 * same reason.
 */
export const UserAvatar = memo(function UserAvatar({
  userId,
  displayName,
  avatarFileName,
  size = "sm",
  className,
  decorative = true,
}: UserAvatarProps) {
  const src = userAvatarSrc({
    userId,
    avatarFileName,
    origin: resolveServerHttpOrigin(),
  });
  const initials = userInitials(displayName);

  return (
    // Keyed on the src: Base UI's Root keeps its image-loading status when the
    // Image child unmounts, so a removed photo would leave the status "loaded"
    // with nothing rendered — no image and a fallback that never shows. A new
    // key remounts the state machine whenever the image identity changes.
    <Avatar key={src ?? "initials"} className={cn(SIZE_CLASSES[size], className)}>
      {src === null ? null : (
        <AvatarImage
          src={src}
          alt={decorative ? "" : displayName}
          {...(decorative ? { "aria-hidden": true } : {})}
        />
      )}
      <AvatarFallback style={userAvatarFallbackStyle(userId)}>{initials}</AvatarFallback>
    </Avatar>
  );
});
